import { supabase } from '../supabaseClient';
import type {
  BetaTesterFeedbackInput,
  BetaTesterFeedbackRow,
} from '../types/betaTesterFeedback';

const SELECT_COLS =
  'id, user_id, full_name, email, good_points, use_cases, improvements, bugs, created_at';

function mapRow(row: Record<string, unknown>): BetaTesterFeedbackRow {
  return {
    id: String(row.id ?? ''),
    userId: typeof row.user_id === 'string' ? row.user_id : null,
    fullName: typeof row.full_name === 'string' ? row.full_name : '',
    email: typeof row.email === 'string' ? row.email : '',
    goodPoints: typeof row.good_points === 'string' ? row.good_points : '',
    useCases: typeof row.use_cases === 'string' ? row.use_cases : '',
    improvements: typeof row.improvements === 'string' ? row.improvements : '',
    bugs: typeof row.bugs === 'string' ? row.bugs : '',
    createdAt: typeof row.created_at === 'string' ? row.created_at : '',
  };
}

function normalizeInput(input: BetaTesterFeedbackInput): {
  full_name: string;
  email: string;
  user_id: string | null;
  good_points: string;
  use_cases: string;
  improvements: string;
  bugs: string;
} {
  const goodPoints = (input.goodPoints ?? '').trim().slice(0, 5000);
  const useCases = (input.useCases ?? '').trim().slice(0, 5000);
  const improvements = (input.improvements ?? '').trim().slice(0, 5000);
  const bugs = (input.bugs ?? '').trim().slice(0, 5000);
  if (!goodPoints && !useCases && !improvements && !bugs) {
    throw new Error('의견 내용을 한 가지 이상 입력해 주세요.');
  }
  return {
    full_name: (input.fullName ?? '').trim().slice(0, 80),
    email: (input.email ?? '').trim().toLowerCase().slice(0, 320),
    user_id: input.userId?.trim() || null,
    good_points: goodPoints,
    use_cases: useCases,
    improvements,
    bugs,
  };
}

export type SubmitBetaTesterFeedbackResult =
  | { ok: true }
  | { ok: false; error: string; missingTable?: boolean };

export async function submitBetaTesterFeedback(
  input: BetaTesterFeedbackInput,
): Promise<SubmitBetaTesterFeedbackResult> {
  try {
    const row = normalizeInput(input);
    const { error } = await supabase.from('platform_beta_tester_feedback').insert(row);
    if (!error) return { ok: true };
    const msg = error.message ?? String(error);
    const missingTable =
      error.code === '42P01' ||
      error.code === 'PGRST205' ||
      /platform_beta_tester_feedback|schema cache|does not exist|relation/i.test(msg);
    return { ok: false, error: msg, missingTable };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { ok: false, error: msg };
  }
}

export async function listBetaTesterFeedback(limit = 1000): Promise<BetaTesterFeedbackRow[]> {
  const { data, error } = await supabase
    .from('platform_beta_tester_feedback')
    .select(SELECT_COLS)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map((r) => mapRow(r as Record<string, unknown>));
}

export type BetaTesterFeedbackCategoryField =
  | 'goodPoints'
  | 'useCases'
  | 'improvements'
  | 'bugs';

const CATEGORY_COLUMN: Record<BetaTesterFeedbackCategoryField, string> = {
  goodPoints: 'good_points',
  useCases: 'use_cases',
  improvements: 'improvements',
  bugs: 'bugs',
};

export type ClearBetaTesterFeedbackResult =
  | { result: 'deleted' }
  | { result: 'cleared'; row: BetaTesterFeedbackRow };

function isFeedbackRowEmpty(row: Pick<
  BetaTesterFeedbackRow,
  'goodPoints' | 'useCases' | 'improvements' | 'bugs'
>): boolean {
  return (
    !row.goodPoints.trim() &&
    !row.useCases.trim() &&
    !row.improvements.trim() &&
    !row.bugs.trim()
  );
}

/** UPDATE RLS가 없을 때: 행 삭제 후 남은 카테고리만 다시 삽입 */
async function clearFeedbackCategoryViaReplace(
  id: string,
  category: BetaTesterFeedbackCategoryField,
): Promise<ClearBetaTesterFeedbackResult> {
  const { data, error } = await supabase
    .from('platform_beta_tester_feedback')
    .select(SELECT_COLS)
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('의견을 찾을 수 없습니다.');

  const current = mapRow(data as Record<string, unknown>);
  const next: BetaTesterFeedbackRow = { ...current, [category]: '' };
  await deleteBetaTesterFeedback(id);

  if (isFeedbackRowEmpty(next)) {
    return { result: 'deleted' };
  }

  const { data: inserted, error: insertError } = await supabase
    .from('platform_beta_tester_feedback')
    .insert({
      user_id: next.userId,
      full_name: next.fullName,
      email: next.email,
      good_points: next.goodPoints,
      use_cases: next.useCases,
      improvements: next.improvements,
      bugs: next.bugs,
      created_at: next.createdAt || undefined,
    })
    .select(SELECT_COLS)
    .single();
  if (insertError) throw insertError;
  return { result: 'cleared', row: mapRow(inserted as Record<string, unknown>) };
}

/** 해당 카테고리 의견만 비움. 네 칸이 모두 비면 행 삭제. */
export async function clearBetaTesterFeedbackCategory(
  id: string,
  category: BetaTesterFeedbackCategoryField,
): Promise<ClearBetaTesterFeedbackResult> {
  const col = CATEGORY_COLUMN[category];
  const { data, error } = await supabase
    .from('platform_beta_tester_feedback')
    .update({ [col]: '' })
    .eq('id', id)
    .select(SELECT_COLS)
    .maybeSingle();

  // UPDATE 정책 미적용·RLS 차단 등으로 0건이면 DELETE+INSERT로 처리
  if (error || !data) {
    return clearFeedbackCategoryViaReplace(id, category);
  }

  const row = mapRow(data as Record<string, unknown>);
  if (isFeedbackRowEmpty(row)) {
    await deleteBetaTesterFeedback(id);
    return { result: 'deleted' };
  }
  return { result: 'cleared', row };
}

export async function deleteBetaTesterFeedback(id: string): Promise<void> {
  const { error } = await supabase.from('platform_beta_tester_feedback').delete().eq('id', id);
  if (error) throw error;
}
